import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Form, Row, Col, Table, Badge, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axiosConfig';
import { useAuth } from '../../../contexts/AuthContext';
import './TermsConditions.css';

const TermsConditions = () => {
    const { hasRole } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [termsList, setTermsList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [approvalAction, setApprovalAction] = useState('');
    const [approvalComment, setApprovalComment] = useState('');
    const [rejectionType, setRejectionType] = useState('rejected_for_edit');

    const [formData, setFormData] = useState({
        title_en: '',
        title_ar: '',
        content_en: '',
        content_ar: '',
        is_active: false
    });

    const fetchTermsConditions = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/terms-conditions');
            const data = response.data.data || response.data || [];
            setTermsList(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching terms:', error);
            toast.error('Failed to load terms and conditions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTermsConditions();
    }, []);

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                title_en: item.title_en || '',
                title_ar: item.title_ar || '',
                content_en: item.content_en || '',
                content_ar: item.content_ar || '',
                is_active: item.is_active || false
            });
        } else {
            setEditingItem(null);
            setFormData({
                title_en: '',
                title_ar: '',
                content_en: '',
                content_ar: '',
                is_active: false
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingItem(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title_en || !formData.title_ar || !formData.content_en || !formData.content_ar) {
            toast.error('Title and content are required in both languages');
            return;
        }

        setSaving(true);
        try {
            if (editingItem) {
                await axiosInstance.put(`/terms-conditions/${editingItem.id}`, formData);
                toast.success('Terms and conditions updated successfully!');
            } else {
                await axiosInstance.post('/terms-conditions', formData);
                toast.success('Terms and conditions created successfully!');
            }
            handleCloseModal();
            fetchTermsConditions();
        } catch (error) {
            console.error('Error saving terms:', error);
            toast.error(error.response?.data?.message || 'Failed to save terms and conditions');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this?')) return;
        try {
            await axiosInstance.delete(`/terms-conditions/${id}`);
            toast.success('Deleted successfully!');
            fetchTermsConditions();
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error(error.response?.data?.message || 'Failed to delete');
        }
    };

    const openApprovalModal = (item, action) => {
        setSelectedItem(item);
        setApprovalAction(action);
        setApprovalComment('');
        setRejectionType('rejected_for_edit');
        setShowApprovalModal(true);
    };

    const handleApprovalSubmit = async () => {
        if (!approvalComment.trim()) {
            toast.error('Please enter a comment');
            return;
        }

        try {
            if (approvalAction === 'approve') {
                await axiosInstance.post(`/terms-conditions/${selectedItem.id}/approve`, {
                    comment: approvalComment
                });
                toast.success('Approved successfully!');
            } else {
                await axiosInstance.post(`/terms-conditions/${selectedItem.id}/reject`, {
                    comment: approvalComment,
                    rejection_type: rejectionType
                });
                toast.success('Rejected successfully!');
            }
            setShowApprovalModal(false);
            fetchTermsConditions();
        } catch (error) {
            console.error('Error processing approval:', error);
            toast.error(error.response?.data?.message || 'Failed to process approval');
        }
    };

    const getStatusBadge = (status) => {
        const statusMapping = {
            approved: { text: 'Approved', variant: 'success' },
            pending: { text: 'Pending', variant: 'warning' },
            rejected: { text: 'Rejected', variant: 'danger' },
            rejected_for_edit: { text: 'Needs Revision', variant: 'info' }
        };
        const { text, variant } = statusMapping[status] || { text: status, variant: 'secondary' };
        return <Badge bg={variant}>{text}</Badge>;
    };

    if (loading) {
        return (
            <Container fluid className="mt-5">
                <div className="text-center">Loading...</div>
            </Container>
        );
    }

    return (
        <Container fluid className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Terms & Conditions</h3>
                {hasRole(['admin', 'it', 'cms_manager']) && (
                    <Button variant="primary" onClick={() => handleOpenModal()}>
                        Add New
                    </Button>
                )}
            </div>

            <Card>
                <Card.Body>
                    <Table responsive striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title (EN)</th>
                                <th>Title (AR)</th>
                                <th>Active</th>
                                <th>Status</th>
                                <th>Created By</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {termsList.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center">No terms and conditions found</td>
                                </tr>
                            ) : (
                                termsList.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>{index + 1}</td>
                                        <td>{item.title_en}</td>
                                        <td>{item.title_ar}</td>
                                        <td>
                                            {item.is_active ? (
                                                <Badge bg="success">Active</Badge>
                                            ) : (
                                                <Badge bg="secondary">Inactive</Badge>
                                            )}
                                        </td>
                                        <td>{getStatusBadge(item.status)}</td>
                                        <td>{item.creator?.name || 'N/A'}</td>
                                        <td>
                                            <Button variant="warning" size="sm" className="me-1" onClick={() => handleOpenModal(item)}>
                                                Edit
                                            </Button>
                                            {hasRole('it') && item.status === 'pending' && (
                                                <>
                                                    <Button variant="success" size="sm" className="me-1" onClick={() => openApprovalModal(item, 'approve')}>
                                                        Approve
                                                    </Button>
                                                    <Button variant="info" size="sm" className="me-1" onClick={() => openApprovalModal(item, 'reject')}>
                                                        Reject
                                                    </Button>
                                                </>
                                            )}
                                            {hasRole('it') && (
                                                <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)}>
                                                    Delete
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem ? 'Edit Terms & Conditions' : 'Add Terms & Conditions'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Title (English)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title_en"
                                        value={formData.title_en}
                                        onChange={handleInputChange}
                                        placeholder="Enter title in English"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Title (Arabic)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title_ar"
                                        value={formData.title_ar}
                                        onChange={handleInputChange}
                                        placeholder="أدخل العنوان بالعربية"
                                        dir="rtl"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Content (English)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={10}
                                        name="content_en"
                                        value={formData.content_en}
                                        onChange={handleInputChange}
                                        placeholder="Enter terms and conditions in English..."
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Content (Arabic)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={10}
                                        name="content_ar"
                                        value={formData.content_ar}
                                        onChange={handleInputChange}
                                        placeholder="أدخل الشروط والأحكام بالعربية..."
                                        dir="rtl"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="is_active"
                                        label="Set as Active (only one can be active)"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={saving}>
                            {saving ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Approval Modal */}
            <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{approvalAction === 'approve' ? 'Approve' : 'Reject'} Terms & Conditions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Comment</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={approvalComment}
                            onChange={(e) => setApprovalComment(e.target.value)}
                            placeholder="Enter your comment..."
                            required
                        />
                    </Form.Group>
                    {approvalAction === 'reject' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Rejection Type</Form.Label>
                            <Form.Select value={rejectionType} onChange={(e) => setRejectionType(e.target.value)}>
                                <option value="rejected_for_edit">Return for Edit</option>
                                <option value="rejected">Permanently Reject</option>
                            </Form.Select>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowApprovalModal(false)}>Cancel</Button>
                    <Button variant={approvalAction === 'approve' ? 'success' : 'danger'} onClick={handleApprovalSubmit}>
                        {approvalAction === 'approve' ? 'Approve' : 'Reject'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default TermsConditions;
